import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getDeals, getActiveFeaturedSlot } from '../api';
import DealCard from '../components/DealCard';
import SEO from '../components/SEO';
import JsonLD from '../components/JsonLD';
import './HomePage.css';

const CUISINES = [
  {slug:'',name:'All',icon:'🍽'},
  {slug:'canadian',name:'Canadian',icon:'🍁'},
  {slug:'indian',name:'Indian',icon:'🍛'},
  {slug:'chinese',name:'Chinese',icon:'🥡'},
  {slug:'italian',name:'Italian',icon:'🍕'},
  {slug:'japanese',name:'Japanese',icon:'🍱'},
  {slug:'mexican',name:'Mexican',icon:'🌮'},
  {slug:'thai',name:'Thai',icon:'🍜'},
  {slug:'vietnamese',name:'Vietnamese',icon:'🍲'},
  {slug:'breakfast',name:'Brunch',icon:'🍳'},
  {slug:'bakery',name:'Café',icon:'☕'},
  {slug:'vegetarian',name:'Vegetarian',icon:'🥗'},
];

export default function HomePage() {
  const [deals, setDeals] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [featuredIsSlot, setFeaturedIsSlot] = useState(false); // true = paid slot, false = regular featured
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [cuisine, setCuisine] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const params = { limit: 8 };
    if (cuisine) params.cuisine_type = cuisine;

    // Check for a paid featured slot first (homepage hero booking)
    // Then load regular deals
    Promise.all([
      getActiveFeaturedSlot().catch(() => ({ slot: null })),
      getDeals(params),
    ]).then(([slotRes, dealsRes]) => {
      if (slotRes.slot) {
        // Paid featured slot takes priority as hero
        setFeatured(slotRes.slot);
        setFeaturedIsSlot(true);
      } else {
        // Fall back to is_featured deal or first deal
        setFeatured(dealsRes.deals.find(x => x.is_featured) || dealsRes.deals[0] || null);
        setFeaturedIsSlot(false);
      }
      setDeals(dealsRes.deals);
    }).catch(() => setDeals([])).finally(() => setLoading(false));
  }, [cuisine]);

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/deals?search=${encodeURIComponent(search)}`);
  };

  return (
    <div className="home">
      <SEO
        title="Winnipeg Restaurant Deals — Up to 50% Off"
        description="Up to 50% off at Winnipeg's best local restaurants. Claim deals instantly with just your email — no account needed. New deals added daily."
        url="/"
      />
      <JsonLD data={{
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        'name': 'halfprice.ca',
        'url': 'https://halfprice.ca',
        'description': 'Winnipeg restaurant deals — up to 50% off at the best local restaurants',
        'potentialAction': {
          '@type': 'SearchAction',
          'target': { '@type': 'EntryPoint', 'urlTemplate': 'https://halfprice.ca/deals?search={search_term_string}' },
          'query-input': 'required name=search_term_string'
        }
      }} />
      {/* Hero */}
      <section className="hero-section">
        <div className="hero-content fade-up">
          <div className="hero-tag">Winnipeg's restaurant deals platform</div>
          <h1 className="hero-title font-display">
            Local flavour,<br /><em>half the price</em>
          </h1>
          <p className="hero-sub">
            Up to 50% off at Winnipeg's best independent restaurants — every single day. Claim instantly, no account needed.
          </p>
          <form className="hero-search" onSubmit={handleSearch}>
            <input
              className="form-control"
              placeholder="Search restaurants, cuisines, dishes…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <button type="submit" className="btn btn-primary btn-lg">Find deals</button>
          </form>
        </div>
        <div className="hero-stats">
          <div className="stat-pill"><strong>247</strong> active deals</div>
          <div className="stat-pill"><strong>89</strong> restaurants</div>
          <div className="stat-pill"><strong>$0</strong> commission ever</div>
          <div className="stat-pill"><strong>50%</strong> max savings</div>
        </div>
      </section>

      {/* Cuisine filter */}
      <section className="cuisine-strip">
        <div className="container">
          <div className="cuisine-scroll">
            {CUISINES.map(c => (
              <button
                key={c.slug}
                className={`cuisine-btn ${cuisine === c.slug ? 'active' : ''}`}
                onClick={() => { setCuisine(c.slug); setLoading(true); }}
              >
                <span className="cuisine-btn-icon">{c.icon}</span>
                <span>{c.name}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Deal */}
      {featured && (
        <section className="section container">
          <div className="section-header">
            <h2 className="section-title">
              {featuredIsSlot ? '⭐ Featured restaurant' : 'Deal of the day 🔥'}
            </h2>
            {featuredIsSlot
              ? <span className="badge badge-brand">Sponsored placement</span>
              : <span className="badge badge-brand">Featured restaurant</span>
            }
          </div>
          <div className="featured-deal">
            <div className="featured-deal-left">
              <div className="featured-cuisine-badge">
                {featured.cuisine_type && <span className="badge badge-muted" style={{fontSize:13}}>
                  {featured.cuisine_type}
                </span>}
                {featured.is_new_restaurant && <span className="badge badge-new">New restaurant 🆕</span>}
              </div>
              <h3 className="featured-deal-biz">{featured.business_name}</h3>
              <h2 className="featured-deal-title font-display">{featured.title}</h2>
              <p className="featured-deal-desc">{featured.description}</p>
              <div className="featured-deal-price">
                <span className="fp-new">${Number(featured.deal_price).toFixed(2)}</span>
                <span className="fp-old">${Number(featured.original_price).toFixed(2)}</span>
                <span className="fp-save">{featured.discount_percent}% off</span>
              </div>
              <Link to={`/deals/${featured.id}`} className="btn btn-primary btn-lg mt-3">
                Claim this deal →
              </Link>
            </div>
            <div className="featured-deal-right">
              {featured.image_url
                ? <img src={featured.image_url} alt={featured.title} />
                : <div className="featured-deal-placeholder">
                    {featured.cuisine_type === 'indian' ? '🍛'
                      : featured.cuisine_type === 'italian' ? '🍕'
                      : featured.cuisine_type === 'japanese' ? '🍱' : '🍽'}
                  </div>
              }
            </div>
          </div>
        </section>
      )}

      {/* Deals Grid */}
      <section className="section container">
        <div className="section-header">
          <h2 className="section-title">{cuisine ? `${cuisine.charAt(0).toUpperCase()+cuisine.slice(1)} deals` : 'Latest deals'}</h2>
          <Link to="/deals" className="btn btn-ghost btn-sm">View all →</Link>
        </div>
        {loading ? (
          <div className="deals-grid">
            {Array.from({length:8}).map((_,i) => (
              <div key={i} className="deal-skeleton">
                <div className="skeleton" style={{height:140}}/>
                <div style={{padding:14}}>
                  <div className="skeleton mb-1" style={{height:12,width:'60%'}}/>
                  <div className="skeleton mb-1" style={{height:16,width:'90%'}}/>
                  <div className="skeleton" style={{height:12,width:'40%'}}/>
                </div>
              </div>
            ))}
          </div>
        ) : deals.length > 0 ? (
          <div className="deals-grid">
            {deals.map(d => <DealCard key={d.id} deal={d} />)}
          </div>
        ) : (
          <div className="empty-state">
            <p style={{fontSize:40,marginBottom:12}}>🍽</p>
            <p>No deals yet in this cuisine — check back soon!</p>
            <Link to="/business/register" className="btn btn-primary mt-2">Be the first restaurant to list</Link>
          </div>
        )}
      </section>

      {/* Why different from Groupon */}
      <section className="why-section">
        <div className="container">
          <div className="why-inner">
            <div className="why-text">
              <div className="why-tag">Why halfprice.ca is different</div>
              <h2 className="font-display why-title">We never take a cut from restaurants</h2>
              <p className="why-desc">Groupon charged restaurants 50% commission — leaving them with only 25 cents on the dollar. We charge restaurants a simple flat monthly fee and keep zero commission on every deal claimed. Restaurants love us. Customers get better deals.</p>
              <div className="why-points">
                <div className="why-point"><span className="why-check">✓</span><span><strong>Zero commission</strong> on every redemption</span></div>
                <div className="why-point"><span className="why-check">✓</span><span><strong>Loyalty stamps</strong> — customers come back 3× more</span></div>
                <div className="why-point"><span className="why-check">✓</span><span><strong>Live in minutes</strong> — no approval delays</span></div>
                <div className="why-point"><span className="why-check">✓</span><span><strong>Flat monthly fee</strong> — predictable cost</span></div>
              </div>
              <Link to="/business/register" className="btn btn-primary btn-lg mt-3">List your restaurant free →</Link>
            </div>
            <div className="why-visual">
              <div className="comparison-card">
                <div className="comp-row header">
                  <span>Feature</span><span>Groupon</span><span>halfprice.ca</span>
                </div>
                <div className="comp-row">
                  <span>Commission</span>
                  <span className="comp-bad">50%</span>
                  <span className="comp-good">0%</span>
                </div>
                <div className="comp-row">
                  <span>Setup fee</span>
                  <span className="comp-bad">High</span>
                  <span className="comp-good">Free</span>
                </div>
                <div className="comp-row">
                  <span>Loyalty system</span>
                  <span className="comp-bad">No</span>
                  <span className="comp-good">Yes ✓</span>
                </div>
                <div className="comp-row">
                  <span>Go live</span>
                  <span className="comp-bad">Weeks</span>
                  <span className="comp-good">Minutes</span>
                </div>
                <div className="comp-row">
                  <span>Cancel anytime</span>
                  <span className="comp-bad">No</span>
                  <span className="comp-good">Yes ✓</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business CTA */}
      <section className="biz-cta-section">
        <div className="container">
          <div className="biz-cta">
            <div className="biz-cta-text">
              <h2 className="font-display">Own a Winnipeg restaurant?</h2>
              <p>Reach thousands of deal-seeking locals every day. No commission. No contracts. Start free today.</p>
              <div className="biz-cta-points">
                <span>✓ First deal free</span>
                <span>✓ Zero commission ever</span>
                <span>✓ Cancel anytime</span>
                <span>✓ Live in minutes</span>
              </div>
            </div>
            <div className="biz-cta-actions">
              <Link to="/business/register" className="btn btn-primary btn-lg">Get listed free →</Link>
              <Link to="/pricing" className="btn btn-outline btn-lg">See pricing</Link>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="section container">
        <h2 className="section-title text-center">How it works for diners</h2>
        <p className="section-sub text-center">Three steps. No account. No credit card.</p>
        <div className="how-grid">
          {[
            {num:'1',icon:'🔍',title:'Browse restaurants',desc:'Filter by cuisine, neighbourhood, or discount level. Find deals at restaurants you already love.'},
            {num:'2',icon:'📧',title:'Claim with your email',desc:'Enter your email and get a unique code instantly. No account, no credit card, no hassle.'},
            {num:'3',icon:'🎉',title:'Show up and save',desc:'Show your code at the restaurant and enjoy up to 50% off. Collect loyalty stamps to unlock bonus deals.'},
          ].map(s => (
            <div key={s.num} className="how-step">
              <div className="how-num">{s.num}</div>
              <div style={{fontSize:32,marginBottom:12}}>{s.icon}</div>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
